/**
 *
 * ModalSignUp
 *
 */

import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import _className from 'classnames';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Hidden from '@material-ui/core/Hidden';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import Typography from '@material-ui/core/Typography';
import Mixpanel from 'utils/mixpanelService';
import { setAuthModalAction } from 'containers/Auth/actions';
import { makeSelectStoredItem } from 'containers/Auth/selectors';
import { showTermsOfUse, showPrivacyPolicy } from 'containers/Main/actions';
import {
  makeSelectOAuthOptions,
  makeSelectClientDetails,
} from 'containers/Main/selectors';
import Divider from 'components/Divider';
import useSiteCopySelector from 'components/useSiteCopySelector';
import SaveAssessmentCard from './SaveAssessmentCard';
import SignUpStepOne from './SignUpStepOne';
import SignUpStepTwo from './SignUpStepTwo';
import QuoteIcon from './images/quote-icon.svg';
import { SeriesPreviewCard } from './SeriesPreviewCard';
import CoursePreviewCard from './CoursePreviewCard';

const useStyles = makeStyles(theme => ({
  container: {
    backgroundColor: 'white',
    maxHeight: '90%',
    overflowY: 'auto',
    position: 'absolute',
    top: props => props.top,
    bottom: props => props.bottom,
    [theme.breakpoints.up('md')]: {
      left: '50%',
      width: 900,
      marginLeft: -450,
    },
    [theme.breakpoints.down('sm')]: {
      width: '90%',
      left: 0,
      right: 0,
      marginLeft: 'auto',
      marginRight: 'auto',
    },
  },
  column: {
    padding: '16px 32px',
  },
  coloredColumn: {
    backgroundColor: '#F9FDFF',
  },
  headerText: {
    paddingTop: 6,
  },
  closeIcon: {
    position: 'absolute',
    right: 12,
    top: 12,
    padding: 6,
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
  },
  dividerItem: {
    flex: 1,
    height: 1,
    backgroundColor: '#CECECE',
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    paddingTop: 12,
  },
  loginText: {
    fontSize: 16,
  },
  link: {
    marginLeft: 5,
    textDecoration: 'none',
    color: theme.palette.primary.main,
    cursor: 'pointer',
  },
  benefitWrapper: {
    display: 'flex',
    marginTop: 32,
    marginBottom: 32,
    alignItems: 'center',
  },
  benefitImage: {
    marginRight: 16,
    height: 49,
    width: 49,
  },
  benefitLabel: {
    fontFamily: 'PoppinsRegular',
    fontSize: 14,
    lineHeight: '19px',
  },
  feedbackWrapper: {
    position: 'relative',
    backgroundColor: '#FFFFFF',
    border: '1px solid #E9ECED',
    borderRadius: 6,
    padding: '24px 24px 24px 56px',
    marginBottom: 16,
    '&::after': {
      top: '100%',
      left: '85%',
      border: 'solid transparent',
      content: '""',
      height: 0,
      width: 0,
      position: 'absolute',
      pointerEvents: 'none',
      borderColor: 'rgba(255, 255, 255, 0)',
      borderTopColor: '#FFFFFF',
      borderLeftWidth: 26,
      borderTopWidth: 13,
      borderRightWidth: 0,
    },
    '&::before': {
      top: '100%',
      left: '85%',
      border: 'solid transparent',
      content: '""',
      height: 0,
      width: 0,
      position: 'absolute',
      pointerEvents: 'none',
      borderColor: 'rgba(233, 236, 237, 0)',
      borderTopColor: '#E9ECED',
      borderLeftWidth: 27,
      borderTopWidth: 14,
      borderRightWidth: 0,
      marginTop: 1,
    },
  },
  quoteIcon: {
    position: 'absolute',
    top: 24,
    left: 24,
  },
  feedbackText: {
    fontFamily: 'PoppinsRegular',
    fontSize: 14,
    lineHeight: '21px',
  },
  feedbackUserWrapper: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  feedbackUserInfo: {
    '& > p:first-child': {
      fontFamily: 'PoppinsBold',
      fontSize: 14,
      lineHeight: '19px',
      textAlign: 'right',
    },
    '& > p:last-child': {
      fontFamily: 'PoppinsRegular',
      fontSize: 12,
      lineHeight: '19px',
      textAlign: 'right',
    },
  },
  feedbackUserImage: {
    height: 45,
    width: 45,
    borderRadius: '50%',
    marginLeft: 16,
    marginRight: 32,
    objectFit: 'cover',
  },
}));

const ModalSignUp = ({
  onClose,
  onSignUp,
  type,
  storedItemPayload,
  positionTop,
}) => {
  const modalPositions = {
    top: positionTop,
    bottom: 'auto',
  };
  const classes = useStyles(modalPositions);
  const dispatch = useDispatch();
  const clientDetails = useSelector(makeSelectClientDetails());
  const storedItem = useSelector(makeSelectStoredItem());
  const data = useRef({});
  const [step, setStep] = useState(0);

  const authOptions = useSelector(makeSelectOAuthOptions());
  const [authModalSiteCopy, signUpSiteCopy] = useSiteCopySelector([
    'authentication-modal',
    'sign-up',
  ]);
  const requiredEmailDomains = _get(
    clientDetails,
    'context.requiredEmailDomains',
    [],
  );
  const shouldLimitEmailDomains = !_isEmpty(requiredEmailDomains);
  const hasClient = !_isEmpty(clientDetails);

  const handleSignInClick = () => {
    Mixpanel.track('Authentication Modal - Switch to Sign In', {
      trigger: storedItem.type,
    });
    dispatch(setAuthModalAction('sign-in'));
  };

  const handleSocialLogin = provider => {
    onSignUp({
      provider: { provider, type: 'popup' },
      subscription: false,
    });
  };
  const handleCaptionClick = action => {
    if (action === 'agreement') dispatch(showTermsOfUse(true));
    else if (action === 'privacy') dispatch(showPrivacyPolicy(true));
  };

  const handleFirstStepSubmit = (email, password) => {
    data.current = {
      email,
      password,
    };
    setStep(1);
  };

  const handleSubmit = ({
    firstName,
    lastName,
    monthYear,
    gender,
    genderOther,
    genderLGBTQp,
    stateOfResidence,
    subscription,
  }) => {
    data.current.firstName = firstName;
    data.current.lastName = lastName;
    if (monthYear) data.current.birthdayMonthYear = monthYear;
    if (gender && gender !== '-1') data.current.gender = gender;
    if (genderOther !== '') data.current.genderOther = genderOther;
    if (genderLGBTQp !== '-1') data.current.genderLGBTQp = genderLGBTQp;
    if (stateOfResidence !== '-1')
      data.current.stateOfResidence = stateOfResidence;
    onSignUp({
      data: data.current,
      provider: null,
      subscription,
    });
  };

  const action = `${_get(authModalSiteCopy, 'pageCopy.sign-up', '')} ${_get(
    authModalSiteCopy,
    ['pageCopy', type],
    '',
  )}`;

  return (
    <Grid container justifyContent="center" className={classes.container}>
      <IconButton
        className={classes.closeIcon}
        onClick={onClose}
        aria-label="Close"
      >
        <CloseIcon />
      </IconButton>
      <Grid item xs={12} md={7} className={classes.column}>
        <Grid container>
          <Grid item xs={12}>
            <Typography
              variant="h5"
              color="primary"
              className={classes.headerText}
              gutterBottom
            >
              {action}
            </Typography>
          </Grid>
          {type === 'move-assessments' && <SaveAssessmentCard />}
          {type === 'series' && (
            <SeriesPreviewCard item={_get(storedItemPayload, 'item')} />
          )}
          {type === 'course' && (
            <CoursePreviewCard item={_get(storedItemPayload, 'item')} />
          )}
          {step === 0 && (
            <SignUpStepOne
              shouldDisplaySAML={_get(
                clientDetails,
                'samlConfiguration.enabled',
                false,
              )}
              authOptions={authOptions}
              onSubmit={handleFirstStepSubmit}
              onCaptionClick={handleCaptionClick}
              onSocialLogin={handleSocialLogin}
              hasClient={hasClient}
              requiredEmailDomains={requiredEmailDomains}
              shouldLimitEmailDomains={shouldLimitEmailDomains}
            />
          )}
          {step === 1 && (
            <SignUpStepTwo email={data.current.email} onSubmit={handleSubmit} />
          )}
          {authOptions.cm && (
            <>
              <Grid item xs={12} className={classes.divider}>
                <div className={classes.dividerItem} />
              </Grid>
              <Grid item xs={12} className={classes.footer}>
                <Typography className={classes.loginText} color="primaryText">
                  {_get(signUpSiteCopy, 'pageCopy.signInModalLabel')}
                  <span
                    role="link"
                    tabIndex={0}
                    className={classes.link}
                    onClick={handleSignInClick}
                  >
                    {_get(signUpSiteCopy, 'pageCopy.signInModalLinkLabel')}
                  </span>
                </Typography>
              </Grid>
            </>
          )}
        </Grid>
      </Grid>
      <Hidden smDown>
        <Grid
          item
          md={5}
          className={_className(classes.column, classes.coloredColumn)}
        >
          <Typography
            variant="h5"
            color="textPrimary"
            gutterBottom
            className={classes.headerText}
          >
            {_get(signUpSiteCopy, 'pageCopy.benefitsHeader')}
          </Typography>
          {_get(signUpSiteCopy, 'pageCopy.benefitsItems', []).map(item => (
            <Box component="div" className={classes.benefitWrapper}>
              <img
                src={item.image}
                className={classes.benefitImage}
                alt={item.label}
              />
              <Typography color="primaryText" className={classes.benefitLabel}>
                {item.label}
              </Typography>
            </Box>
          ))}
          <Divider top={32} bottom={24} />
          <Box component="div" className={classes.feedbackWrapper}>
            <img src={QuoteIcon} className={classes.quoteIcon} alt="Quote" />
            <Typography color="primaryText" className={classes.feedbackText}>
              {_get(signUpSiteCopy, 'pageCopy.feedbackText')}
            </Typography>
          </Box>
          <Box component="div" className={classes.feedbackUserWrapper}>
            <Box component="div" className={classes.feedbackUserInfo}>
              <Typography color="textPrimary">
                {_get(signUpSiteCopy, 'pageCopy.feedbackName')}
              </Typography>
              <Typography color="textPrimary">
                {_get(signUpSiteCopy, 'pageCopy.feedbackSubtitle')}
              </Typography>
            </Box>
            <img
              src={_get(signUpSiteCopy, 'pageCopy.feedbackImage')}
              className={classes.feedbackUserImage}
              alt={_get(signUpSiteCopy, 'pageCopy.feedbackName')}
            />
          </Box>
        </Grid>
      </Hidden>
    </Grid>
  );
};

ModalSignUp.propTypes = {
  onClose: PropTypes.func,
  onSignUp: PropTypes.func,
  type: PropTypes.string,
  positionTop: PropTypes.string,
};

export default ModalSignUp;
